import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search as SearchIcon, ArrowRight, Loader2, MapPin, Crosshair, ChefHat } from "lucide-react";
import { api } from "@/lib/api";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/BusinessSection";
import { MEDIA } from "@/lib/media";

export default function SearchPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (e) => {
    e?.preventDefault();
    const term = q.trim();
    if (!term) return;
    setBusy(true);
    try {
      const [states, recipes, firearms] = await Promise.all([
        api.exploreStates().catch(() => []),
        api.recipes().catch(() => []),
        api.publicFirearms({ q: term }).then((d) => d.firearms).catch(() => []),
      ]);
      const t = term.toLowerCase();
      setRes({
        states: (states || []).filter((s) => s.state.toLowerCase().includes(t)).slice(0, 8),
        recipes: (recipes || []).filter((r) => (r.name || "").toLowerCase().includes(t) || (r.game || "").toLowerCase().includes(t)).slice(0, 8),
        firearms: (firearms || []).slice(0, 8),
      });
    } finally { setBusy(false); }
  };

  const empty = res && !res.states.length && !res.recipes.length && !res.firearms.length;

  return (
    <div className="bg-paper min-h-screen" data-testid="search-page">
      <SiteHeader />
      <section className="relative bg-charcoal text-bone overflow-hidden grain">
        <div className="absolute inset-0 z-0">
          <img src={MEDIA.stateHero} alt="" aria-hidden="true" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/80 to-charcoal/50" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-5 sm:px-10 py-16 text-center">
          <span className="hr-overline text-blaze">Search HomeRange</span>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-3">Find your next hunt.</h1>
          <form onSubmit={run} className="relative mt-7">
            <SearchIcon size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-charcoal/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="States, species, gear, firearms, recipes…" autoFocus
              className="w-full bg-bone text-charcoal pl-12 pr-28 py-4 text-base outline-none" data-testid="search-input" />
            <button type="submit" className="absolute right-2 top-1/2 -translate-y-1/2 bg-blaze text-white px-5 py-2.5 text-sm font-semibold hover:bg-blaze-light transition-colors" data-testid="search-submit">Search</button>
          </form>
        </div>
      </section>

      <main className="max-w-[1200px] mx-auto px-5 sm:px-10 py-12">
        {busy ? (
          <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-forest" /></div>
        ) : res === null ? (
          <p className="text-center text-charcoal/50">Search across states, species, gear, firearms research and recipes.</p>
        ) : empty ? (
          <p className="text-center text-charcoal/60 py-12" data-testid="search-empty">No matches for &ldquo;{q}&rdquo;. Try a state, species, or gear category.</p>
        ) : (
          <div className="space-y-10" data-testid="search-results">
            {res.states.length > 0 && (
              <Group title="States" icon={MapPin}>
                {res.states.map((s) => (
                  <button key={s.state} onClick={() => navigate(`/states/${encodeURIComponent(s.state)}`)} className="bg-white border border-saddle/20 p-4 text-left hover:border-forest transition-colors" data-testid={`search-state-${s.state}`}>
                    <h3 className="font-serif text-lg text-forest">{s.state}</h3>
                    <span className="inline-flex items-center gap-1.5 text-blaze text-sm mt-2">Plan hunt <ArrowRight size={13} /></span>
                  </button>
                ))}
              </Group>
            )}
            {res.firearms.length > 0 && (
              <Group title="Firearms Research" icon={Crosshair}>
                {res.firearms.map((f) => (
                  <button key={f.id} onClick={() => navigate(`/firearms/${f.id}`)} className="bg-white border border-saddle/20 p-4 text-left hover:border-forest transition-colors" data-testid={`search-firearm-${f.id}`}>
                    <h3 className="font-serif text-lg text-forest">{f.manufacturer} {f.model}</h3>
                    <p className="text-xs text-charcoal/55 mt-1">{[f.category, f.caliber].filter(Boolean).join(" · ")}</p>
                  </button>
                ))}
              </Group>
            )}
            {res.recipes.length > 0 && (
              <Group title="Recipes" icon={ChefHat}>
                {res.recipes.map((r) => (
                  <button key={r.id} onClick={() => navigate(`/recipes/${r.id}`)} className="bg-white border border-saddle/20 p-4 text-left hover:border-forest transition-colors" data-testid={`search-recipe-${r.id}`}>
                    <h3 className="font-serif text-lg text-forest">{r.name}</h3>
                    <p className="text-xs text-charcoal/55 mt-1">{[r.game, r.cut].filter(Boolean).join(" · ")}</p>
                  </button>
                ))}
              </Group>
            )}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

function Group({ title, icon: Icon, children }) {
  return (
    <section>
      <h2 className="font-serif text-2xl text-forest flex items-center gap-2 mb-4"><Icon size={20} className="text-blaze" /> {title}</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">{children}</div>
    </section>
  );
}
